import { useCallback, useMemo, useReducer, useEffect } from 'react';

import PropTypes from 'prop-types';

import CheckDepositHistory from '#client/components/CheckDepositHistory';
import FormStepModal from '#client/components/FormStepModal';
import { CHECK_DEPOSIT_STEPS } from '#client/constants/checkDeposit';
import { MODAL_NAMES } from '#client/constants/modals';
import useBreakpoints from '#client/hooks/useBreakpoints';
import { FormStepProvider } from '#client/hooks/useFormStep';
import { useMitekErrorHandler } from '#client/hooks/useMitekErrorHandler';
import { MitekSdkProvider } from '#client/hooks/useMitekSdk';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';

import CheckDepositSubmissionOverview from '../CheckDepositSubmissionOverview';
import CheckDepositEducation from './components/CheckDepositEducation';
import CheckDepositLoading from './components/CheckDepositLoading';
import CheckDepositOverview from './components/CheckDepositOverview';
import ExitConfirmModal from './components/ExitConfirmModal';

import { MODAL_ACTION } from './constants/actions';
import { CHECK_DEPOSIT_UNIT_STATUS } from './constants/status';
import { depositReducer, initState } from './reducer/checkDepositReducer';

export default function CheckDepositModal({ handleClose, isResubmit, actionsKit = null }) {
  const {
    sizes: { isMobile }
  } = useBreakpoints();
  const { createErrorToast } = useToastContext();
  const { clearAllModal, openModal } = useModalContext();
  const { mitekErrorHandler } = useMitekErrorHandler();
  const [depositData, dispatchDepositData] = useReducer(depositReducer, null, initState);

  const { title, loading, currentFormStep, status, reason, depositId, amount } = depositData;

  const handleDepositAnother = () => {
    dispatchDepositData({ type: MODAL_ACTION.DEPOSIT_ANOTHER });
  };

  const handleCloseAndClearModals = useCallback(() => {
    handleClose();
    clearAllModal();
    if (typeof actionsKit?.resetSearchParameters === 'function') {
      actionsKit.resetSearchParameters();
    }
    if (typeof actionsKit?.submitCheckDepositSuccessHandler === 'function') {
      actionsKit?.submitCheckDepositSuccessHandler();
    }
  }, [handleClose, clearAllModal, actionsKit]);

  // create toast when after submitted
  const createToastHandler = () => {
    const toastConfig = {
      title: `Check ${depositId ? `ID ${depositId}` : ''} ${
        status === CHECK_DEPOSIT_UNIT_STATUS.REJECTED ? 'was rejected' : 'needs review'
      }`,
      description: reason,
      secondaryAction: {
        label: 'Review',
        size: 'sm',
        onClick: () => {
          openModal({
            component: CheckDepositHistory
          });
        }
      }
    };

    createErrorToast(toastConfig);
    handleCloseAndClearModals();
  };

  const loadCompletedHandler = () => {
    dispatchDepositData({ type: MODAL_ACTION.STOP_LOADING });
    // if the current step is OVERVIEW, that mean the API submitted
    // and got the Rejected or AwaitingCustomerConfirmation status
    // so after the loading finish, it should navigate to the dashboard
    // and show the error toast
    if (currentFormStep === CHECK_DEPOSIT_STEPS.OVERVIEW) {
      createToastHandler();
    }
  };

  const handleCloseModal = () => {
    if (currentFormStep === CHECK_DEPOSIT_STEPS.OVERVIEW) {
      openModal({
        component: ExitConfirmModal,
        name: MODAL_NAMES.CHECK_DEPOSIT_EXIT_CONFIRM_MODAL,
        title: 'Are you sure?',
        description: 'If you leave now, you’ll lose your progress and have to start over.',
        okText: 'Yes, cancel',
        cancelText: ' No, keep going',
        onOk: handleCloseAndClearModals
      });
    } else {
      handleCloseAndClearModals();
    }
  };

  useEffect(() => {
    if (isResubmit) {
      dispatchDepositData({ type: MODAL_ACTION.DEPOSIT_ANOTHER });
    }
  }, [isResubmit]);

  const currentStep = useMemo(() => {
    switch (currentFormStep) {
      case CHECK_DEPOSIT_STEPS.EDUCATION:
        return <CheckDepositEducation />;
      case CHECK_DEPOSIT_STEPS.OVERVIEW:
        return <CheckDepositOverview handleClose={handleCloseAndClearModals} />;
      case CHECK_DEPOSIT_STEPS.SUBMISSION:
        return (
          <CheckDepositSubmissionOverview
            amount={amount}
            handleClose={handleCloseAndClearModals}
            handleDepositAnother={handleDepositAnother}
          />
        );
      default:
        return <CheckDepositOverview handleClose={handleCloseAndClearModals} />;
    }
  }, [amount, currentFormStep, handleCloseAndClearModals]);

  const hideBackButton = currentFormStep !== CHECK_DEPOSIT_STEPS.EDUCATION;

  return (
    <MitekSdkProvider handleError={mitekErrorHandler}>
      <FormStepModal
        currentStep={currentFormStep}
        disableBack={hideBackButton}
        disableCloseModal={loading}
        handleBack={handleClose}
        handleClose={handleCloseModal}
        isHideBackButton={hideBackButton}
        title={title}
      >
        <FormStepProvider
          value={{
            dispatchDepositData,
            depositData
          }}
        >
          {loading ? (
            <CheckDepositLoading
              height={isMobile ? '150px' : '250px'}
              loadCompletedHandler={loadCompletedHandler}
              text="Finalizing check deposit"
              url="/animation/lottie/money_Tetris_noText.json"
            />
          ) : (
            currentStep
          )}
        </FormStepProvider>
      </FormStepModal>
    </MitekSdkProvider>
  );
}

CheckDepositModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  isResubmit: PropTypes.bool,
  actionsKit: PropTypes.shape({
    resetSearchParameters: PropTypes.func,
    submitCheckDepositSuccessHandler: PropTypes.func
  })
};
